import React from 'react';
import {useQueryGraphProps} from './results-graph/use-query-graph-props';
import styles from '../query-layout.module.css';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../components/layout/resizable-layout';
import {QueryResultsHeader} from './query-results-header/query-results-header';
import {QueryResults} from './query-results';
import statsPanelStyles from './right-panel/right-panel.module.css';
import {RightPanel} from './right-panel/right-panel';
import {CompletedQueryInnerContentProps} from './completed-query-content-with-presentations';
import {GraphPresentationPropsLoaded} from './results-graph/use-graph-presentation-props';
import {useGraphPresentationActions} from './results-graph/use-graph-presentation-actions';
import {useIsCantDisplayGraph} from './results-graph/use-is-cant-display-graph';
import {CompletedQueryLayout} from './completed-query-layout';
import {useGraphSelectionProps} from '../../../../components/graph/graph-logic/use-graph-selection-props';
import {useQueryLayoutProps} from '../../hooks/use-query-layout-props';
import {usePathsResultListProps} from './right-panel/paths-result-list/use-paths-result-list-props';
import {QueryEditor} from '../query-editor/query-editor';
import {useSidebarTabs} from '../../../../hooks/use-sidebar-tabs';

interface Props extends Omit<CompletedQueryInnerContentProps, 'presentations'> {
    graphPresentationProps: GraphPresentationPropsLoaded;
    sidebarTabsProps: ReturnType<typeof useSidebarTabs>;
}

export function CompletedQueryLoadedContent({
    selectedQuery,
    globalVersions,
    maxPathLengthOptions,
    graphPresentationProps,
    sidebarTabsProps
}: Props) {
    const graphSelectionProps = useGraphSelectionProps();
    const queryLayoutProps = useQueryLayoutProps({
        selectedQuery,
        selectedNodes: graphSelectionProps.selectedNodes
    });
    const resultsListProps = usePathsResultListProps({selectedQuery});

    const resultsGraphProps = useQueryGraphProps({
        selectedQuery,
        maxPathLengthOptions,
        selectedPresentation: graphPresentationProps.selectedPresentation,
        selectedPresentationDetails: graphPresentationProps.presentationDetails,
        selectionProps: graphSelectionProps,
        resultsListProps
    });

    const presentationActionsProps = useGraphPresentationActions({
        graphPresentationProps,
        graphProps: resultsGraphProps,
        queryId: selectedQuery.id
    });

    const isCantDisplayGraphProps = useIsCantDisplayGraph({
        graphProps: resultsGraphProps,
        maxPathLengthOptions
    });

    return (
        <CompletedQueryLayout
            selectedQuery={selectedQuery}
            resultsListProps={resultsListProps}
            queryLayoutProps={queryLayoutProps}
            selectedNodes={graphSelectionProps.selectedNodes}
            clearNodeSelection={resultsGraphProps.graphActions.clearSelectionAction}
            clearResultListItemSelection={
                resultsGraphProps.graphActions.clearListResultSelectionAction
            }
            maxPathLength={resultsGraphProps.graphSettings.maxPathLength}
            canFetchNodeStructure={resultsGraphProps.canFetchNodeStructure}
        >
            <CustomResizableLayout
                localStorageKey="queries.rightPanel.layoutSize"
                settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
                alignment="horizontal"
                className={styles.selectedQueryBody}
                fixedPanelSize={
                    queryLayoutProps.isQueryRightPanelOpen ? undefined : {panelIndex: 1, size: 0}
                }
            >
                {(resizableLayoutProps) => (
                    <React.Fragment>
                        <ResizablePanel
                            index={0}
                            {...resizableLayoutProps}
                            className={styles.queryResults}
                        >
                            <QueryEditor
                                isCollapsed={queryLayoutProps.isBodyCollapsed}
                                toggleIsCollapsed={queryLayoutProps.toggleIsBodyCollapsed}
                                selectedQuery={selectedQuery}
                                globalVersions={globalVersions}
                                sidebarTabsProps={sidebarTabsProps}
                            />

                            <QueryResultsHeader
                                maxLengthOptions={maxPathLengthOptions}
                                layoutAlgorithm={
                                    resultsGraphProps.layoutAndCameraProps.layoutAlgorithm
                                }
                                selectLayoutAlgorithm={
                                    resultsGraphProps.graphActions.rebuildLayoutAction
                                }
                                isCustomLayout={
                                    resultsGraphProps.layoutAndCameraProps.isCustomLayout
                                }
                                graphSettingsProps={resultsGraphProps.graphSettings}
                                graphPresentationProps={graphPresentationProps}
                                presentationEditedProps={resultsGraphProps.presentationEditedProps}
                                queryId={selectedQuery.id}
                                presentationActionsProps={presentationActionsProps}
                                isCalculatingLayout={
                                    !resultsGraphProps.layoutAndCameraProps.visibleNodesPositions
                                }
                                clearUnsavedPresentationDetails={
                                    resultsGraphProps.clearUnsavedChanges
                                }
                                isCantDisplayGraphProps={isCantDisplayGraphProps}
                                graphActionsProps={resultsGraphProps.graphActions}
                            />

                            <QueryResults
                                graphProps={resultsGraphProps}
                                resultsListProps={resultsListProps}
                                selectionProps={graphSelectionProps}
                                maxPathLengthOptions={maxPathLengthOptions}
                                isCantDisplayGraphProps={isCantDisplayGraphProps}
                            />
                        </ResizablePanel>

                        {queryLayoutProps.isQueryRightPanelOpen && (
                            <ResizablePanel
                                index={1}
                                {...resizableLayoutProps}
                                className={statsPanelStyles.root}
                            >
                                <RightPanel
                                    closePanel={queryLayoutProps.closeQueryRightPanel}
                                    queryId={selectedQuery.id}
                                    viewKind={resultsGraphProps.graphSettings.viewKind}
                                    maxPathLength={resultsGraphProps.graphSettings.maxPathLength}
                                    resultsListProps={resultsListProps}
                                    selectedGraphNodes={graphSelectionProps.selectedNodes}
                                    selectGraphNodes={
                                        resultsGraphProps.graphActions.nodeSelectionChangedAction
                                    }
                                    setHoveredNodes={graphSelectionProps.setHoveredNodes}
                                    onListResultSelected={
                                        resultsGraphProps.graphActions.listResultSelectedAction
                                    }
                                    invisibleNodes={
                                        resultsGraphProps.hiddenNodesProps.isHiddenNodesVisible
                                            ? []
                                            : resultsGraphProps.hiddenNodesProps.hiddenNodes
                                    }
                                />
                            </ResizablePanel>
                        )}
                    </React.Fragment>
                )}
            </CustomResizableLayout>
        </CompletedQueryLayout>
    );
}
